/**
 * JUNTAR DUAS VERSÕES DA MESMA FICHA (R1A, revisão da fase 5, 06/10/2026).
 *
 * O caso que ensinou: com sinal fraco, o banco gravava a ficha e a resposta não
 * voltava. No «Sincronizar» seguinte o app achava que outro aparelho tinha
 * mexido, punha por cima a versão do banco (sem as pesagens novas) e mandava a
 * deste aparelho para «Conflitos». Pesagem feita no curral sumia da tela.
 *
 * Agora, num conflito DE VERDADE (dois aparelhos mexeram na mesma ficha):
 *  - as LISTAS (pesagens, lotes, porteiras, gastos, parcelas, histórico) se
 *    juntam pelo id: o que só existe numa versão entra; o que existe nas duas
 *    igual fica uma vez só. Nada se perde, nada se duplica;
 *  - o que existe nas duas com conteúdo DIFERENTE (a mesma pesagem corrigida
 *    nos dois aparelhos, o produtor trocado nos dois) vira uma DIVERGÊNCIA: a
 *    junta fica com a do banco, e a pessoa escolhe, campo a campo, se quer a
 *    deste aparelho. Só o que diverge é perguntado.
 *
 * Limite aceito: pesagem EXCLUÍDA num aparelho e mantida no outro volta a
 * aparecer — perder pesagem é pior do que mostrar uma a mais (o gestor exclui
 * de novo). Sem tela e sem armazenamento: testado em testes/juntar.mjs.
 */
import { dataBR, num, reais } from './numeros'
import { SITUACOES, type Ficha } from './tipos'

export interface Divergencia {
  /** `situacao`, `produtor`… ou `registros:<id>`, `lotes:<id>`… */
  campo: string
  rotulo: string
  /** em português, para a pessoa escolher */
  deste: string
  doBanco: string
}

export interface Juncao {
  /** a ficha juntada: listas unidas, cabeçalho do banco */
  junta: Ficha
  /** o que ficou diferente e espera a escolha da pessoa */
  divergencias: Divergencia[]
  /** a junta tem algo que o banco ainda não tem (precisa subir) */
  mudouDoBanco: boolean
}

/** Campos de cabeçalho comparados um a um (o número é sempre o do banco). */
const CABECALHO: [keyof Ficha, string][] = [
  ['situacao', 'Situação'], ['finalidade', 'Finalidade'], ['data', 'Data'], ['produtor', 'Produtor'],
  ['obs', 'Observação'], ['forma', 'Forma de pesagem'], ['financeiroPct', 'Financeiro % da ficha'],
  ['funruralPct', 'Funrural % da ficha'], ['condicao', 'Condição de pagamento'], ['gta', 'GTA'],
  ['destino', 'Destino antigo'], ['custoFechado', 'Custo fechado'], ['pesadoPor', 'Pesado por'], ['emitente', 'Emitente'],
]

/** As listas que se juntam por id, e como cada item se chama na tela. */
type Lista = 'registros' | 'lotes' | 'porteiras' | 'custos' | 'pagamentos'
const LISTAS: Lista[] = ['lotes', 'porteiras', 'registros', 'custos', 'pagamentos']

const igual = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

function textoDoCampo(campo: keyof Ficha, v: unknown): string {
  if (v === undefined || v === null || v === '') return '(vazio)'
  const o = v as Record<string, unknown>
  switch (campo) {
    case 'situacao': return SITUACOES[v as keyof typeof SITUACOES] ?? String(v)
    case 'data': return dataBR(String(v))
    case 'produtor': return String(o['nome'] || '(sem nome)')
    case 'condicao': return [o['forma'], o['obs']].filter(Boolean).join(' · ') || '(vazio)'
    case 'destino': return String(o['nome'] || '(vazio)')
    case 'custoFechado': return `fechado por ${String(o['por'] ?? '')}`
    case 'emitente': return String(o['nome'] || '(vazio)')
    case 'financeiroPct': case 'funruralPct': return `${num(Number(v), 2)}%`
    default: return typeof v === 'object' ? JSON.stringify(v).slice(0, 80) : String(v)
  }
}

function rotuloDoItem(lista: Lista, x: Record<string, unknown>, f: Ficha): string {
  if (lista === 'registros') return `Pesagem nº ${String(x['n'] ?? '?')}`
  if (lista === 'lotes') return `Lote ${String(x['categoria'] ?? '')}`
  if (lista === 'porteiras') return `Porteira ${String(x['nome'] ?? '')}`
  if (lista === 'custos') return `Gasto ${String(x['descricao'] ?? '')}`
  const i = f.pagamentos.findIndex((p) => p.id === x['id'])
  return `Parcela ${i >= 0 ? i + 1 : ''}`.trim()
}

function textoDoItem(lista: Lista, x: Record<string, unknown> | undefined, f: Ficha): string {
  if (!x) return '(não existe)'
  if (lista === 'registros') {
    const lote = f.lotes.find((l) => l.id === x['loteId'])?.categoria ?? ''
    const kg = x['kg'] === null || x['kg'] === undefined ? 'sem peso' : `${num(Number(x['kg']), 0)} kg`
    return `${String(x['qtd'])} cab · ${kg}${lote ? ` · ${lote}` : ''}${x['obs'] ? ` · ${String(x['obs'])}` : ''}`
  }
  if (lista === 'lotes') return `${String(x['categoria'])} · ${x['preco'] === null ? 'sem preço' : reais(Number(x['preco']))} · ${String(x['base'])}`
  if (lista === 'porteiras') return `${String(x['nome'] || '(sem nome)')} (${String(x['tipo'])})`
  if (lista === 'custos') return `${String(x['descricao'])} · ${reais(Number(x['valor']))}`
  return `${reais(Number(x['valor']))} · vence ${dataBR(String(x['vencimento'] ?? ''))}`
}

/**
 * Junta a versão do BANCO com a deste APARELHO (a ficha como está no momento
 * da resposta do servidor — inclusive o que foi pesado enquanto ela subia).
 */
export function juntarFichas(banco: Ficha, local: Ficha): Juncao {
  const junta: Ficha = structuredClone(banco)
  const divergencias: Divergencia[] = []

  for (const lista of LISTAS) {
    const doBanco = (banco[lista] ?? []) as unknown as Record<string, unknown>[]
    const daqui = (local[lista] ?? []) as unknown as Record<string, unknown>[]
    const saida = structuredClone(doBanco)
    const ids = new Map(doBanco.map((x) => [x['id'], x]))
    for (const x of daqui) {
      const noBanco = ids.get(x['id'])
      if (!noBanco) { saida.push(structuredClone(x)); continue }
      if (!igual(noBanco, x)) {
        divergencias.push({
          campo: `${lista}:${String(x['id'])}`, rotulo: rotuloDoItem(lista, x, local),
          deste: textoDoItem(lista, x, local), doBanco: textoDoItem(lista, noBanco, banco),
        })
      }
    }
    // lista que não existe em nenhum dos dois (ficha sem porteiras) continua sem existir
    if (banco[lista] !== undefined || saida.length) (junta as unknown as Record<string, unknown>)[lista] = saida
  }

  // pesagem nova dos dois aparelhos pode ter ganho o mesmo nº de ordem: a que
  // veio daqui ganha o próximo livre (o nº nunca é reaproveitado)
  const usados = new Set<number>()
  let maior = junta.registros.reduce((m, r) => Math.max(m, r.n), 0)
  const idsDoBanco = new Set(banco.registros.map((r) => r.id))
  for (const r of junta.registros) {
    if (usados.has(r.n) && !idsDoBanco.has(r.id)) r.n = ++maior
    usados.add(r.n)
  }
  junta.registros.sort((a, b) => a.n - b.n)

  // histórico: o de cada lado, sem repetir, em ordem de hora
  const vistos = new Set<string>()
  junta.historico = [...banco.historico, ...local.historico].filter((h) => {
    const k = JSON.stringify(h)
    if (vistos.has(k)) return false
    vistos.add(k)
    return true
  }).sort((a, b) => a.em.localeCompare(b.em))

  junta.versao = Math.max(banco.versao ?? 1, local.versao ?? 1)

  for (const [campo, rotulo] of CABECALHO) {
    if (!igual(banco[campo], local[campo])) {
      divergencias.push({ campo, rotulo, deste: textoDoCampo(campo, local[campo]), doBanco: textoDoCampo(campo, banco[campo]) })
    }
  }

  return { junta, divergencias, mudouDoBanco: !mesmoConteudo(junta, banco) }
}

/** O conteúdo da ficha, sem as marcas de quando e de quem é a cópia. */
export function mesmoConteudo(a: Ficha, b: Ficha): boolean {
  const limpar = (f: Ficha) => {
    const { alteradaEm: _a, revServidor: _r, enviosSemResposta: _e, ...resto } = f
    return resto
  }
  return igual(limpar(a), limpar(b))
}

/**
 * Aplica as escolhas da pessoa: para cada `campo` da lista, fica o valor
 * DESTE aparelho (`local`) por cima da ficha `atual`. O resto não muda.
 */
export function aplicarEscolhas(atual: Ficha, local: Ficha, campos: string[]): Ficha {
  const f = structuredClone(atual) as unknown as Record<string, unknown>
  const l = local as unknown as Record<string, unknown>
  for (const campo of campos) {
    const [lista, id] = campo.split(':') as [string, string | undefined]
    if (id === undefined) {
      if (campo in l) f[campo] = structuredClone(l[campo])
      else delete f[campo]
      continue
    }
    const deLa = ((l[lista] ?? []) as Record<string, unknown>[]).find((x) => String(x['id']) === id)
    if (!deLa) continue
    const aqui = (f[lista] ?? []) as Record<string, unknown>[]
    const i = aqui.findIndex((x) => String(x['id']) === id)
    if (i >= 0) aqui[i] = structuredClone(deLa)
    else aqui.push(structuredClone(deLa))
    f[lista] = aqui
  }
  return f as unknown as Ficha
}
